"use client";

import { Trans } from "@lingui/react/macro";
import { FragmentOf, readFragment } from "gql.tada";
import { DatabaseIcon, PlusIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  SidebarGroupAction,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { SA_POLICIES } from "@/lib/authorization/policies/search-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import CreateProjectDialog from "../../dialog/create-project-dialog";
import CollapsibleProjectGroup from "./collapsible-project-group";
import { SearchAnalyticsProjectItemFragment } from "./fragments";
import SaSortableProjects from "./sa-sortable-projects";
import SidebarMenuButtonWithPath from "./sidebar-menu-button-with-path";
import { SidebarSearchUnfilteredOnly } from "./sidebar-search-context";
import type { CollapsibleState } from "./types";

type Props = {
  workspaceId: string;
  projectsData: ReadonlyArray<
    FragmentOf<typeof SearchAnalyticsProjectItemFragment>
  >;
  initialGroupState: CollapsibleState;
  onboardingSessions: ReadonlyArray<{
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  }>;
  completedOnboardingSessions: ReadonlyArray<{
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  }>;
  workspaceUsedColors?: string[];
};

export default function SaSidebarGroup({
  workspaceId,
  projectsData,
  initialGroupState,
  onboardingSessions,
  completedOnboardingSessions,
  workspaceUsedColors,
}: Props) {
  const currentPathname = usePathname();
  const checkPolicy = useCheckPolicy();
  const validProjectsData = projectsData || [];

  // ビューデータのキーを生成（ビュー名の変更を検知するため）
  const dataKey = validProjectsData
    .map((data) => {
      const project = readFragment(SearchAnalyticsProjectItemFragment, data);
      if (project?.searchAnalytics?.views) {
        return project.searchAnalytics.views
          .map((v) => `${v.id}:${v.name}`)
          .join(",");
      }
      return "";
    })
    .join("|");

  const canEditProjects = checkPolicy(SA_POLICIES["sa-project:edit"]).ok;
  const canEditBigQuery = checkPolicy(SA_POLICIES["sa-bigquery:edit"]).ok;

  return (
    <CollapsibleProjectGroup
      groupType="sa"
      title={<Trans>SEARCH ANALYTICS</Trans>}
      initialState={initialGroupState}
      action={
        canEditProjects ? (
          <CreateProjectDialog
            workspaceId={workspaceId}
            type="sa"
            workspaceUsedColors={workspaceUsedColors}
            trigger={
              <SidebarGroupAction>
                <PlusIcon className="w-4 h-4" />
                <span className="sr-only">
                  <Trans>New Project</Trans>
                </span>
              </SidebarGroupAction>
            }
          />
        ) : undefined
      }
    >
      <SidebarMenu>
        <SaSortableProjects
          key={dataKey}
          workspaceId={workspaceId}
          projectsData={validProjectsData}
          currentPathname={currentPathname}
          onboardingSessions={onboardingSessions}
          completedOnboardingSessions={completedOnboardingSessions}
        />
        {canEditBigQuery ? (
          <SidebarSearchUnfilteredOnly>
            <SidebarMenuItem>
              <SidebarMenuButtonWithPath
                activePattern={`.*/${encodeURIComponent(
                  workspaceId,
                )}/settings/sa-bigquery`}
                asChild
              >
                <Link
                  href={`/${encodeURIComponent(workspaceId)}/settings/sa-bigquery`}
                >
                  <DatabaseIcon className="w-4 h-4" />
                  <Trans>Search Analytics BigQuery</Trans>
                </Link>
              </SidebarMenuButtonWithPath>
            </SidebarMenuItem>
          </SidebarSearchUnfilteredOnly>
        ) : null}
      </SidebarMenu>
    </CollapsibleProjectGroup>
  );
}
